import { useState } from 'react';
import { Plane, ChevronDown, Lock, AlertTriangle } from 'lucide-react';

const fmt = (c) => ((c || 0) / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const fmtDt = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleDateString('de-DE') + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

// Aug 2026 — StB-Reisen-Ansicht: read-only Reisen des Mandanten mit komplettem
// Rechenweg + Rechtsgrundlage je Position (Reisekosten-Modul).
export default function AdvisorTravelSection({ data }) {
  const [open, setOpen] = useState(null);
  const trips = data?.trips || [];
  const s = data?.summary || {};

  return (
    <div className="space-y-4" data-testid="advisor-travel-section">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ['Reisen gesamt', s.count ?? 0],
          ['Davon verbucht', s.final_count ?? 0],
          ['Verbuchte Pauschalen', fmt(s.final_total_cents)],
          ['Vorsteuer (§ 13 UStG)', fmt(s.final_vat_cents)],
        ].map(([label, val]) => (
          <div key={label} className="bg-white border border-zinc-200 rounded-xl p-3">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="text-lg font-semibold" data-testid={`advisor-travel-kpi-${label}`}>{val}</p>
          </div>
        ))}
      </div>

      {trips.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-sm text-slate-500"
             data-testid="advisor-travel-empty">
          <Plane className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          Keine Geschäftsreisen im gewählten Jahr erfasst.
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl divide-y">
          {trips.map((t) => (
            <div key={t.id}>
              <button className="w-full flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-left text-sm hover:bg-slate-50"
                      data-testid={`advisor-travel-row-${t.id}`}
                      onClick={() => setOpen(open === t.id ? null : t.id)}>
                <span>
                  <strong>{t.title}</strong>
                  <span className="ml-2 text-slate-500">{t.destination_place} ({t.country})</span>
                  <span className="block text-xs text-slate-400">{fmtDt(t.start_at)} – {fmtDt(t.end_at)}{t.purpose ? ` · ${t.purpose}` : ''}</span>
                </span>
                <span className="flex items-center gap-3">
                  <span className="font-mono">{fmt(t.totals?.total_cents)}</span>
                  {t.status === 'final'
                    ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 text-xs"><Lock className="h-3 w-3" />Verbucht</span>
                    : <span className="rounded-full bg-zinc-50 border border-zinc-200 text-zinc-500 px-2 py-0.5 text-xs">Entwurf</span>}
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open === t.id ? 'rotate-180' : ''}`} />
                </span>
              </button>
              {open === t.id && (
                <div className="px-4 pb-4 space-y-2" data-testid={`advisor-travel-details-${t.id}`}>
                  {(t.positions || []).map((p) => (
                    <div key={p.key} className="rounded-lg bg-slate-50 border border-zinc-200 p-3 text-sm">
                      <div className="flex justify-between font-medium">
                        <span>{p.label}{p.vat_cents > 0 && <span className="text-slate-500 font-normal"> · Vorsteuer {fmt(p.vat_cents)}</span>}</span>
                        <span className="font-mono">{fmt(p.amount_cents)}</span>
                      </div>
                      <p className="mt-1 text-xs text-slate-600" data-testid={`advisor-travel-trace-${t.id}-${p.key}`}>{p.trace}</p>
                      <p className="text-[11px] text-slate-400">{p.legal_basis}</p>
                    </div>
                  ))}
                  {(t.warnings || []).map((w, i) => (
                    <p key={i} className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{w}
                    </p>
                  ))}
                  <p className="text-[11px] text-slate-400">
                    {t.status === 'final'
                      ? `Verbucht am ${fmtDt(t.finalized_at)} — ${t.generated_expense_ids.length} Buchung(en) in den Ausgaben (Konten via Kontenrahmen-Mapping).`
                      : 'Entwurf — noch keine Buchungen erzeugt.'}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400">
        Read-only-Ansicht: Berechnungen erfolgen nach dem ivoliq-Regelkatalog (24h-Regel, Zwölftel,
        Kürzungen, Caps, Dreimonatsfrist) mit stichtagsbezogenen gesetzlichen Sätzen — der Rechenweg
        je Position ist die Dokumentation iSd § 13 Abs 4 UStG (Zeit, Ziel, Zweck, Betrag).
      </p>
    </div>
  );
}
